<script>
import { mapGetters } from 'vuex'

import Layout from '@layouts/main.vue'

import Translatable from '@src/mixins/translatable.js'
import ImageMixin from '@src/mixins/imageMixin.js'
import Resizable from '@src/mixins/resizable.js'
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js'
import AuthorLabel from '@src/mixins/author.js'
import { sharedBlocksMixin } from '@src/mixins/sharedBlocksMixin.js'

import SharedBlockSlot from '@components/sharedBlocks/SharedBlockSlot.vue'

export default {
  page: {
    title: 'Privacy Policy',
    meta: [{ name: 'description', content: 'Privacy Policy Page.' }],
  },
  components: {
    Layout,
    SharedBlockSlot,
  },
  mixins: [
    Translatable,
    ImageMixin,
    Resizable,
    SanitizerMixin,
    AuthorLabel,
    sharedBlocksMixin,
  ],
  computed: {
    ...mapGetters('privacyPolicy', ['pageData']),
    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen
          ? this.pageData.mainImage.web
          : this.pageData.mainImage.app
      }
      return null
    },
  },
  provide() {
    const provided = {}
    Object.defineProperty(provided, 'sortedSharedBlocks', {
      enumerable: true,
      get: () => this.sortedSharedBlocks,
    })
    return {
      sharedBlocksProvider: provided,
    }
  },
  methods: {
    getSeoText(obj) {
      if (!obj) return ''
      return obj[this.currentLanguage] || obj.en || ''
    },
  },
}
</script>

<template>
  <Layout>
    <section
      v-if="pageData && pageData.content && pageImg"
      class="header-section justify-center"
      :style="getBackground(pageImg.url)"
      :title="getSeoText(pageImg.title)"
      role="img"
      :aria-label="getSeoText(pageImg.alt)"
    ></section>
    <section class="description-banner-section justify-center">
      <div class="description-container">
        <p class="authorf container" v-html="authorLabel(pageData)"></p>
        <div class="description-header-container">
          <h1
            class="description-title"
            v-html="getTranslatedContent('title', pageData.content)"
          ></h1>
          <h3
            class="description-subtitle"
            v-html="getTranslatedContent('subtitle', pageData.content)"
          ></h3>
        </div>
        <div class="content-container">
          <div
            class="content-text"
            v-html="description(pageData.content)"
          ></div>
        </div>
      </div>
    </section>
    <SharedBlockSlot :slot-index="1" />
    <SharedBlockSlot :slot-index="2" />
    <SharedBlockSlot :slot-index="3" />
    <SharedBlockSlot :slot-index="4" />
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.header-section {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 550px;
  width: 100%;
}

.description-banner-section {
  margin-top: -335px;

  & .description-container {
    width: 1140px;
    min-height: 600px;
    height: auto;
    display: flex;
    flex-direction: column;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
    padding: 15px;
  }
}

.description-header-container {
  width: 100%;
  min-height: 120px;
  height: auto;
  background: $dark-blue;
  padding: 10px 0 20px;
  border-radius: 10px;

  .description-title {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin: 0 22px;
    color: $white;
  }

  .description-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin: 0 25px;
    color: $white;
  }
}

::v-deep.content-container {
  margin-top: 30px;
  padding: 0 15px;
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  color: $black;
  // opacity: 0.8;
  user-select: none;
  & p:empty {
    height: 15px;
    margin: 0;
  }

  & p:not(:last-child):not(:empty) {
    margin-bottom: 15px;
  }

  & p > br {
    display: none;
  }
  & p:has(br:last-child)::after {
    content: '';
    display: block;
    height: 15px;
  }

  & em {
    font-style: italic;
  }

  & a {
    color: $gray;
    text-decoration: none;

    &:hover {
      color: $orange;
    }
  }

  & ul {
    margin-left: 20px;
    list-style: initial;
  }

  & strong {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
  }

  & .ql-align-center {
    text-align: center;
  }
}

@media only screen and (max-width: $screen-width-xs) {
  .container {
    width: 100%;
  }

  .header-section {
    height: 244px;
  }

  .description-header-container {
    min-height: 125px;
    height: auto;
    margin-top: -20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .description-title {
      font-family: Open Sans Bold, sans-serif;
      font-style: normal;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 20px 8px 10px;
      color: $white;
      text-align: center;
    }

    .description-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
      text-align: center;
    }
  }

  .description-banner-section {
    margin: -130px 0 40px;
    & .description-container {
      width: 100%;
      padding: 0;
      box-shadow: none;
    }
  }
}
</style>
